import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Grid, Link, Stack, Alert,
} from '@mui/material';
import { initKeycloak } from '../keycloak';
import { commercialGet, commercialPost } from '../utils/commercialApi';
import OnboardingResult from './commercial/OnboardingResult';
import { INK } from '../utils/commercialColors';

// Always the real public domain, regardless of where this dashboard is
// currently served from (localhost, staging, ...) - same reasoning as the
// hardcoded https://qrproofs.com in ShowFacility.jsx's badge embed code:
// this link is meant to be shared externally, not reflect the current origin.
const DEMO_LINK_BASE = 'https://qrproofs.com/';

// Same auth+fetch boilerplate as CommercialDashboard.jsx / MainBoard.jsx -
// this codebase has no shared auth hook, each dashboard page does its own,
// so this follows that convention rather than introducing a new pattern
// for just this one page.
export default function AgentDetail() {
  const { id } = useParams();
  const [kcInstance, setKcInstance] = useState(null);
  const [token, setToken] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agent, setAgent] = useState(null);
  const [recentClients, setRecentClients] = useState([]);
  const loginRequestedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const kc = await initKeycloak();
      window.history.replaceState(null, '', window.location.origin + window.location.pathname);

      if (!kc.authenticated) {
        if (loginRequestedRef.current) return;
        loginRequestedRef.current = true;
        kc.login({ redirectUri: window.location.href });
        return;
      }
      if (!kc.hasRealmRole('ROLE_ADMIN') && !kc.hasRealmRole('ROLE_SALES')) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      setKcInstance(kc);
      setToken(kc.token ?? '');
    })().catch(console.error);
  }, []);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    commercialGet(`/agents/${id}`, token)
      .then((data) => {
        setAgent(data.agent);
        setRecentClients(data.recent_clients || []);
      })
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token, id]);

  if (forbidden) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Accès refusé.</Typography>;
  }
  if (loading) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Chargement…</Typography>;
  }
  if (error) {
    return <Typography sx={{ mt: 4, textAlign: 'center', color: '#d03b3b' }}>{error}</Typography>;
  }
  if (!agent) {
    return <Typography sx={{ mt: 4, textAlign: 'center' }}>Ambassadeur introuvable.</Typography>;
  }

  const commission = (Number(agent.mrr_generated) || 0) * (Number(agent.commission_rate) || 0) / 100;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Link component={RouterLink} to="/admin/commercial" sx={{ display: 'block', mb: 1 }}>
            ← Retour au dashboard
          </Link>
          <Typography variant="h4">{agent.first_name} {agent.last_name}</Typography>
          <Typography variant="body2" sx={{ color: INK.muted }}>
            {agent.zone} — code referral : <strong>{agent.referral_code}</strong>
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => kcInstance?.logout({ redirectUri: `${window.location.origin}/` })}>
          Logout
        </Button>
      </Box>

      {kcInstance?.hasRealmRole('ROLE_ADMIN') && (
        <WelcomeCard agent={agent} token={kcInstance.token} />
      )}

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Votre lien de démo</Typography>
        <Typography variant="body2" sx={{ color: INK.muted, mb: 1.5 }}>
          Partagez ce lien : toute demande de démo passée dessus vous est automatiquement attribuée.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: '#f4f6f8',
              border: `1px solid ${INK.gridline}`,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: 14,
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {DEMO_LINK_BASE}?ref={agent.referral_code}
          </Box>
          <Button
            variant="contained"
            onClick={async () => {
              const link = `${DEMO_LINK_BASE}?ref=${agent.referral_code}`;
              try {
                await navigator.clipboard.writeText(link);
                alert('Lien copié !');
              } catch (error) {
                console.error('[referral link copy] failed:', error);
                alert('Impossible de copier le lien.');
              }
            }}
          >
            📋 Copier
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          {[
            ['Leads', agent.leads],
            ['Essais', agent.trials],
            ['Payants', agent.payants],
            ['MRR généré', `${Number(agent.mrr_generated).toLocaleString('fr-FR')} €`],
            ['Commission', `${commission.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`],
          ].map(([label, value]) => (
            <Grid size={{ xs: 6, md: 2.4 }} key={label}>
              <Typography variant="caption" sx={{ color: INK.muted, textTransform: 'uppercase' }}>{label}</Typography>
              <Typography variant="h6" sx={{ color: INK.primary, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Derniers clients</Typography>
        <Stack spacing={1}>
          {recentClients.map((c) => (
            <Box key={c.facility_id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Link component={RouterLink} to={`/admin/commercial/facility/${c.facility_id}`}>
                {c.facility_name}
              </Link>
              <Typography variant="body2" sx={{ color: INK.muted }}>
                {c.converted_at ? new Date(c.converted_at).toLocaleDateString('fr-FR') : '—'}
              </Typography>
            </Box>
          ))}
          {recentClients.length === 0 && (
            <Typography variant="body2" sx={{ color: INK.muted }}>Aucun client pour l'instant.</Typography>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

// Admins: when the welcome e-mail last went out, and re-sending it (which
// also re-checks the Keycloak account and ROLE_SALES - utils/ambassadorWelcome.ts).
function WelcomeCard({ agent, token }) {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [sentAt, setSentAt] = React.useState(agent.welcome_sent_at);
  const resend = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const data = await commercialPost(`/agents/${agent.id}/welcome`, token, {});
      setResult(data.onboarding);
      if (data.onboarding?.welcomeSent) setSentAt(new Date().toISOString());
    } catch (err) {
      setError(err.message === 'Agent is inactive' ? 'Ambassadeur désactivé : réactivez-le d’abord.' : err.message);
    } finally { setBusy(false); }
  };
  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Accès et e-mail de bienvenue</Typography>
      <Typography variant="body2" sx={{ color: INK.muted, mb: 1.5 }}>
        {sentAt
          ? `E-mail de bienvenue envoyé le ${new Date(sentAt).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}.`
          : 'E-mail de bienvenue jamais envoyé.'}
        {' '}Le renvoyer vérifie aussi son compte et son accès ambassadeur.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <OnboardingResult result={result} email={agent.email} />
      <Button variant="outlined" onClick={resend} disabled={busy || !agent.email}>
        {busy ? 'Envoi…' : 'Renvoyer l’e-mail de bienvenue'}
      </Button>
    </Paper>
  );
}

